import {
  Box,
  ResponsiveSection,
  Banner,
  Stack,
  Text,
} from '@stitch-fix/mode-react';
import type { NextPage, GetServerSideProps } from 'next';
import { GreenhouseJobsList } from '../../../components/GreenhouseJobsList/GreenhouseJobsList';
import { Layout } from '../../../components/Layout';
import { SEOData, type SEODataProps } from '../../../components/SEOData';
import {
  PageHeading,
  type PageHeadingProps,
} from '../../../components/PageHeading/PageHeading';
import {
  getGreenhouseDepartments,
  type GreenhouseDepartment,
} from '../../../utils/requests/greenhouse/requests';
import { careersJobsData } from './data';

export type CareersJobsPageProps = {
  seoData: SEODataProps;
  departments: GreenhouseDepartment[];
  pageHeading: PageHeadingProps;
  description: string;
};

const OPERATIONS_WAREHOUSE_ASSOCIATE_JOB = {
  absolute_url: 'https://stitchfix-warehouse-careers.fountain.com/',
  title: 'Warehouse Associate',
  location: {
    name: 'Phoenix, AZ; Atlanta, GA; Indianapolis, IN',
  },
};

const addStaticEntriesToDepartments = (
  departments: GreenhouseDepartment[],
): GreenhouseDepartment[] => {
  return departments.map((dept: GreenhouseDepartment) => {
    if (
      dept.name.toLowerCase() === 'operations' &&
      !dept.jobs.includes(OPERATIONS_WAREHOUSE_ASSOCIATE_JOB)
    ) {
      return {
        ...dept,
        jobs: [...dept.jobs, OPERATIONS_WAREHOUSE_ASSOCIATE_JOB],
      };
    }

    return dept;
  });
};

export const getServerSideProps: GetServerSideProps<
  CareersJobsPageProps
> = async () => {
  let departments: GreenhouseDepartment[] | undefined;

  try {
    const greenhouseDepartmentsResult = await getGreenhouseDepartments();

    departments = greenhouseDepartmentsResult?.departments ?? [];
  } catch (error) {
    departments = [];
  }

  return {
    props: {
      ...careersJobsData,
      departments: addStaticEntriesToDepartments(departments),
    },
  };
};

const CareersJobsPage: NextPage<CareersJobsPageProps> = props => {
  return (
    <Layout viewEventTrackingName="corporate">
      <SEOData {...props.seoData} />
      <ResponsiveSection
        as="main"
        // setting this id allows the "Skip to page content" link to render from knit
        id="knit-main-content"
        mt="0"
        width={{ sm: 'full-bleed' }}
        gutter="none"
      >
        <Stack
          mb={{ sm: '2rem', lg: '3rem' }}
          spacing={{ sm: '2rem', lg: '3rem' }}
        >
          <PageHeading {...props.pageHeading} />

          <Box
            px={1}
            mx={{ sm: 'auto' }}
            style={{
              // add max width to help with legibility and prevent text from
              // running on
              maxWidth: '750px',
            }}
          >
            <Text align="center" setting="body-small">
              {props.description}
            </Text>
          </Box>
          {props.departments.length > 0 ? (
            <GreenhouseJobsList departments={props.departments} />
          ) : (
            <Banner variant="error-border" m={1}>
              Uh oh! Our jobs aren&apos;t loading, please try again later.
            </Banner>
          )}
        </Stack>
      </ResponsiveSection>
    </Layout>
  );
};

export default CareersJobsPage;
